import React, { useState, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Link,
  Input,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useToast,
  Image,
  useDisclosure,
  Grid,
  Spinner,
  Checkbox,
} from "@chakra-ui/react";
import { Global, css } from "@emotion/react";
import { motion } from "framer-motion";
import { Evento } from "../types/Event";
import { eventService } from "../services/eventService";
import { bookingService } from "../services/bookingService";
import BookingModal from "../components/BookingModal";
import GiftCardModal from "../components/GiftCardModal";
import WorldMap from "../components/WorldMap";
import PassportModal from "../components/PassportModal";
import { getUserInfo, authConfig, ClientPrincipal, getUserDisplayName, AUTHORIZED_EMAIL } from "../authConfig";

// Motion components
const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);
const MotionVStack = motion(VStack);

const LandingPage: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newsletterNome, setNewsletterNome] = useState("");
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterUpdates, setNewsletterUpdates] = useState(false);
  const [newsletterPrivacy, setNewsletterPrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [eventi, setEventi] = useState<Evento[]>([]);
  const [loadingEventi, setLoadingEventi] = useState(true);
  const [selectedEvento, setSelectedEvento] = useState<Evento | null>(null);
  const [isWaitingList, setIsWaitingList] = useState(false);
  const [user, setUser] = useState<ClientPrincipal | null>(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [userPastEventIds, setUserPastEventIds] = useState<number[]>([]);
  const [userFutureEventIds, setUserFutureEventIds] = useState<number[]>([]);
  const [giftCardOpen, setGiftCardOpen] = useState(false);
  const [impersonateEmail, setImpersonateEmail] = useState("");
  const [viewAsEmail, setViewAsEmail] = useState("");
  const [passportOpen, setPassportOpen] = useState(false);
  const [visitedEvents, setVisitedEvents] = useState<Evento[]>([]);
  const chiSiamoRef = useRef<HTMLDivElement>(null);
  const cucineRef = useRef<HTMLDivElement>(null);
  const serateRef = useRef<HTMLDivElement>(null);
  const contattiRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  useEffect(() => {
    loadEventi();
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update visited events when eventi or userPastEventIds change
  useEffect(() => {
    if (eventi.length > 0 && userPastEventIds.length > 0) {
      const visitedEventsData = eventi.filter(e => 
        e.EventoId && userPastEventIds.includes(e.EventoId)
      );
      setVisitedEvents(visitedEventsData);
    }
  }, [eventi, userPastEventIds]);

  // Check for pending booking intent after login
  useEffect(() => {
    if (!userLoaded || loadingEventi) return;
    
    const bookingIntent = sessionStorage.getItem("bookingIntent");
    if (bookingIntent && user) {
      try {
        const { eventoId, isWaitingList: waitingList } = JSON.parse(bookingIntent);
        sessionStorage.removeItem("bookingIntent");
        
        // Find the event and restore booking modal
        const evento = eventi.find(e => e.EventoId === eventoId);
        if (evento) {
          setSelectedEvento(evento);
          setIsWaitingList(waitingList);
        }
        
        // Show login success toast - minimal style
        toast({
          duration: 2500,
          position: "bottom",
          render: () => (
            <Box
              bg="gray.800"
              color="gray.300"
              px={4}
              py={2}
              border="1px solid"
              borderColor="whiteAlpha.200"
              fontSize="xs"
              textTransform="uppercase"
              letterSpacing="widest"
            >
              Accesso effettuato ✓
            </Box>
          ),
        });
      } catch (e) {
        sessionStorage.removeItem("bookingIntent");
      }
    }
  }, [userLoaded, loadingEventi, user, eventi, toast]);

  const loadUser = async () => {
    const userInfo = await getUserInfo();
    setUser(userInfo);
    setUserLoaded(true);
    
    // Load user's bookings (both past and future)
    if (userInfo?.userDetails) {
      loadUserBookings(userInfo.userDetails);
    }
  };
  
  const loadUserBookings = async (email: string) => {
    try {
      const data = await bookingService.getMyBookings(email);
      const now = new Date();
      
      // Get confirmed past bookings
      const pastEventIds = data.prenotazioni
        .filter(p => p.Stato === 'Confermata' && p.EventoId && new Date(p.DataEvento || '') < now)
        .map(p => p.EventoId as number);
      setUserPastEventIds(pastEventIds);
      
      // Get confirmed future bookings
      const futureEventIds = data.prenotazioni
        .filter(p => p.Stato === 'Confermata' && p.EventoId && new Date(p.DataEvento || '') >= now)
        .map(p => p.EventoId as number);
      setUserFutureEventIds(futureEventIds);
      
      // Get full event details for visited events (for passport)
      const visitedEventsData = eventi.filter(e => 
        e.EventoId && pastEventIds.includes(e.EventoId)
      );
      setVisitedEvents(visitedEventsData);
    } catch (err) {
      console.error("Errore nel caricamento delle prenotazioni utente:", err);
      setUserPastEventIds([]);
      setUserFutureEventIds([]);
      setVisitedEvents([]);
    }
  };
  
  const handleViewAsUser = () => {
    if (impersonateEmail.trim()) {
      setViewAsEmail(impersonateEmail.trim().toUpperCase());
      loadUserBookings(impersonateEmail.trim().toUpperCase());
    } else {
      setViewAsEmail("");
      if (user?.userDetails) {
        loadUserBookings(user.userDetails);
      }
    }
  };

  const handleLogout = () => {
    window.location.href = `${authConfig.logoutUrl}?post_logout_redirect_uri=${encodeURIComponent("/")}`;
  };

  const loadEventi = async () => {
    try {
      setLoadingEventi(true);
      const data = await eventService.getEventi();
      // Load all events (including past events for the world map) and sort by date
      const allEvents = data
        .sort((a, b) => new Date(a.DataEvento).getTime() - new Date(b.DataEvento).getTime());
      setEventi(allEvents);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingEventi(false);
    }
  };

  const scrollToSerate = () => {
    serateRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContatti = () => {
    contattiRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBook = (evento: Evento, waitingList: boolean) => {
    setSelectedEvento(evento);
    setIsWaitingList(waitingList);
  };

  const handleBookingClose = () => {
    setSelectedEvento(null);
    setIsWaitingList(false);
  };

  const handleBookingSuccess = () => {
    loadEventi();
    setSelectedEvento(null);
  };

  // Pre-fill newsletter form when user logs in
  useEffect(() => {
    if (user) {
      const displayName = getUserDisplayName(user);
      if (displayName) setNewsletterNome(displayName);
      if (user.userDetails) setNewsletterEmail(user.userDetails);
    }
  }, [user]);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterNome || !newsletterEmail || !newsletterUpdates || !newsletterPrivacy) return;
    
    setIsSubmitting(true);
    
    try {
      // Check if email already exists
      const checkResponse = await fetch(`/api/newsletter/check?email=${encodeURIComponent(newsletterEmail)}`);
      if (!checkResponse.ok) {
        throw new Error("Errore nella verifica");
      }
      const checkData = await checkResponse.json();
      
      if (checkData.exists) {
        toast({
          duration: 4000,
          isClosable: true,
          render: () => (
            <Box bg="gray.700" color="white" px={4} py={3} borderRadius="md" boxShadow="lg" sx={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif' }}>
              <Text fontSize="sm" textTransform="uppercase">Questa email è già iscritta alla newsletter.</Text>
            </Box>
          ),
        });
        setIsSubmitting(false);
        return;
      }
      
      // Create newsletter subscription
      const createResponse = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Nome: newsletterNome,
          Email: newsletterEmail,
          Privacy: newsletterPrivacy,
          Updates: newsletterUpdates,
        }),
      });
      
      if (!createResponse.ok) {
        if (createResponse.status === 409) {
          toast({
            duration: 4000,
            isClosable: true,
            render: () => (
              <Box bg="gray.700" color="white" px={4} py={3} borderRadius="md" boxShadow="lg" sx={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif' }}>
                <Text fontSize="sm" textTransform="uppercase">Questa email è già iscritta alla newsletter.</Text>
              </Box>
            ),
          });
          setIsSubmitting(false);
          return;
        }
        throw new Error("Errore nell'iscrizione");
      }
      
      toast({
        duration: 5000,
        isClosable: true,
        render: () => (
          <Box bg="black" border="1px solid" borderColor="green.400" px={6} py={4} borderRadius="none" boxShadow="lg" sx={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif' }}>
            <HStack spacing={4}>
              <Box
                w="40px"
                h="40px"
                borderRadius="full"
                border="2px solid"
                borderColor="green.400"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <Text fontSize="lg" color="green.400">✓</Text>
              </Box>
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" fontWeight="500" color="white" textTransform="uppercase" letterSpacing="widest">ISCRIZIONE COMPLETATA</Text>
                <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide">Riceverai le nostre novità via email</Text>
              </VStack>
            </HStack>
          </Box>
        ),
      });
      setNewsletterNome("");
      setNewsletterEmail("");
      setNewsletterUpdates(false);
      setNewsletterPrivacy(false);
      onClose();
    } catch (error) {
      toast({
        title: "ERRORE",
        description: "Si è verificato un errore. Riprova più tardi.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const navLinks = [
    { label: "PROSSIME SERATE", action: scrollToSerate },
    { label: "NEWSLETTER", action: onOpen },
    { label: "GIFT CARD", action: () => setGiftCardOpen(true) },
    { label: "CONTATTI", action: scrollToContatti },
  ];

  return (
    <>
      <Global
        styles={css`
          html {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          html::-webkit-scrollbar {
            display: none;
          }
        `}
      />
      {/* Fixed background image */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        backgroundImage="url('/tavolata.jpg')"
        backgroundSize="cover"
        backgroundPosition="center"
        zIndex={-2}
      />
      {/* Fixed dark overlay */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="blackAlpha.800"
        zIndex={-1}
      />
      
      {/* All scrollable content */}
      <Box position="relative" zIndex={1}>
      {/* Hero Section */}
      <Box minH="100vh" position="relative" overflow="hidden">
      {/* Animated background elements */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        overflow="hidden"
        pointerEvents="none"
        zIndex={1}
      >
        {/* Gradient orbs */}
        <MotionBox
          position="absolute"
          top="-20%"
          right="-10%"
          w="600px"
          h="600px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%)"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <MotionBox
          position="absolute"
          bottom="-10%"
          left="-10%"
          w="500px"
          h="500px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%)"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.4, 0.2, 0.4],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Subtle grid pattern */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.03}
          backgroundImage="linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)"
          backgroundSize="50px 50px"
        />
      </Box>

      {/* Main content */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        minH="100vh"
        position="relative"
        zIndex={2}
        px={6}
        py={12}
      >
        {/* User header */}
        <Box
          position="absolute"
          top={4}
          right={4}
          zIndex={10}
        >
          {user ? (
            <VStack align="flex-end" spacing={2}>
              <HStack spacing={4}>
                <Text
                  fontSize="xs"
                  color="gray.400"
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  {getUserDisplayName(user)}
                </Text>
                <Button
                  as={RouterLink}
                  to="/le-mie-prenotazioni"
                  size="xs"
                  bg="transparent"
                  color="gray.400"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  borderRadius="none"
                  fontSize="xs"
                  letterSpacing="wide"
                  textTransform="uppercase"
                  _hover={{
                    bg: "whiteAlpha.100",
                    color: "white",
                  }}
                >
                  LE MIE PRENOTAZIONI
                </Button>
                {user.userDetails === AUTHORIZED_EMAIL && (
                  <Button
                    as={RouterLink}
                    to="/serate"
                    size="xs"
                    bg="transparent"
                    color="gray.400"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                    borderRadius="none"
                    fontSize="xs"
                    letterSpacing="wide"
                    textTransform="uppercase"
                    _hover={{
                      bg: "whiteAlpha.100",
                      color: "white",
                    }}
                  >
                    ADMIN
                  </Button>
                )}
                <Button
                  size="xs"
                  bg="transparent"
                  color="gray.400"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  borderRadius="none"
                  fontSize="xs"
                  letterSpacing="wide"
                  textTransform="uppercase"
                  _hover={{
                    bg: "whiteAlpha.100",
                    color: "white",
                  }}
                  onClick={handleLogout}
                >
                  ESCI
                </Button>
              </HStack>
              {/* Admin Impersonation */}
              {user.userDetails === AUTHORIZED_EMAIL && (
                <HStack spacing={2} w="100%" maxW="400px">
                  <Input
                    value={impersonateEmail}
                    onChange={(e) => setImpersonateEmail(e.target.value)}
                    placeholder="Email utente"
                    bg="rgba(0, 0, 0, 0.3)"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                    borderRadius="none"
                    color="white"
                    _placeholder={{ color: "gray.500" }}
                    _hover={{ borderColor: "whiteAlpha.400" }}
                    _focus={{ borderColor: "white", boxShadow: "none" }}
                    size="xs"
                    fontSize="xs"
                  />
                  <Button
                    onClick={handleViewAsUser}
                    size="xs"
                    bg="transparent"
                    color="gray.400"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                    borderRadius="none"
                    fontSize="xs"
                    letterSpacing="wide"
                    textTransform="uppercase"
                    _hover={{ bg: "whiteAlpha.100", color: "white" }}
                    minW="60px"
                  >
                    {viewAsEmail ? "RESET" : "SIMULA"}
                  </Button>
                  {viewAsEmail && (
                    <Text color="cyan.400" fontSize="2xs" whiteSpace="nowrap">
                      👀 {viewAsEmail}
                    </Text>
                  )}
                </HStack>
              )}
            </VStack>
          ) : (
            <Button
              size="xs"
              bg="transparent"
              color="gray.400"
              border="1px solid"
              borderColor="whiteAlpha.300"
              borderRadius="none"
              fontSize="xs"
              letterSpacing="wide"
              textTransform="uppercase"
              _hover={{
                bg: "whiteAlpha.100",
                color: "white",
              }}
              onClick={() => window.location.href = `${authConfig.loginUrl}?post_login_redirect_uri=${encodeURIComponent(window.location.pathname)}`}
            >
              ACCEDI
            </Button>
          )}
        </Box>

        {/* Logo animation */}
        <MotionBox
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            type: "spring", 
            stiffness: 200, 
            damping: 20,
            delay: 0.2 
          }}
          mb={8}
        >
          <Box
            borderRadius="full"
            overflow="hidden"
            boxSize={{ base: "140px", md: "180px", lg: "200px" }}
            border="3px solid"
            borderColor="whiteAlpha.200"
            boxShadow="0 0 60px rgba(255, 255, 255, 0.2)"
          >
            <Image
              src="/logo.png"
              alt="Scarpetta Home Restaurant"
              w="100%"
              h="100%"
              objectFit="cover"
            />
          </Box>
        </MotionBox>

        {/* Main title with staggered letter animation */}
        <MotionHeading
          as="h1"
          fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
          fontWeight="500"
          letterSpacing="wider"
          textAlign="center"
          color="white"
          textTransform="uppercase"
          mb={4}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Scarpetta
          <br />
          Home Restaurant
        </MotionHeading>

        {/* Animated divider */}
        <MotionBox
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          w={{ base: "60px", md: "80px" }}
          h="1px"
          bg="white"
          my={6}
          borderRadius="full"
        />

        {/* Tagline */}
        <MotionText
          fontSize={{ base: "sm", md: "md", lg: "lg" }}
          color="gray.400"
          textAlign="center"
          fontWeight="300"
          letterSpacing="widest"
          textTransform="uppercase"
          mb={12}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
        >
          Esplora il mondo un piatto alla volta
        </MotionText>

        {/* Navigation links */}
        <MotionVStack
          spacing={4}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2 }}
        >
          <HStack
            spacing={{ base: 4, md: 8 }}
            flexWrap="wrap"
            justify="center"
          >
            {navLinks.map((link, index) => (
              <MotionBox
                key={link.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.5, 
                  delay: 1.4 + index * 0.15 
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <Box onClick={link.action} cursor="pointer">
                  <NavButton label={link.label} />
                </Box>
              </MotionBox>
            ))}
          </HStack>
        </MotionVStack>

      </Flex>
      </Box>
      {/* Transition Section */}
      <Box
        h={{ base: "30vh", md: "40vh" }}
        position="relative"
        overflow="hidden"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {/* Animated lines coming from sides */}
        <MotionBox
          position="absolute"
          left={0}
          top="50%"
          h="1px"
          bg="linear-gradient(to right, transparent, white)"
          initial={{ width: 0 }}
          whileInView={{ width: "40%" }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
        <MotionBox
          position="absolute"
          right={0}
          top="50%"
          h="1px"
          bg="linear-gradient(to left, transparent, white)"
          initial={{ width: 0 }}
          whileInView={{ width: "40%" }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
        
        {/* Center diamond */}
        <MotionBox
          initial={{ scale: 0, rotate: 45 }}
          whileInView={{ scale: 1, rotate: 45 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8, type: "spring", stiffness: 200 }}
        >
          <Box
            w={{ base: "8px", md: "10px" }}
            h={{ base: "8px", md: "10px" }}
            bg="white"
            transform="rotate(45deg)"
          />
        </MotionBox>
      </Box>

      {/* Chi Siamo Section */}
      <Box
        ref={chiSiamoRef}
        py={{ base: 16, md: 24 }}
        px={6}
        position="relative"
      >
        <VStack spacing={6} mb={12}>
          <MotionHeading
            as="h2"
            fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
            fontWeight="500"
            letterSpacing="wider"
            textAlign="center"
            color="white"
            textTransform="uppercase"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Chi Siamo
          </MotionHeading>
          <MotionBox
            w={{ base: "40px", md: "60px" }}
            h="1px"
            bg="white"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          />
        </VStack>

        <MotionBox
          maxW="900px"
          mx="auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <VStack spacing={6} align="stretch">
            <Text
              color="gray.400"
              fontSize={{ base: "sm", md: "md" }}
              lineHeight="tall"
              textAlign="center"
            >
              Siamo Ilaria e Federica. Ci siamo conosciute dieci anni fa su un volo per il Mozambico, e da allora cibo e viaggi sono stati una costante nelle nostre vite.
            </Text>
            
            <Text
              color="gray.400"
              fontSize={{ base: "sm", md: "md" }}
              lineHeight="tall"
              textAlign="center"
            >
              Ilaria è appassionata di spezie, con cui riesce sempre a dare un "twist" particolare anche ai piatti apparentemente più banali. La cosa preferita di Federica, invece, sono i dolci: non c'è gioia più grande del profumo di cardamom buns appena sfornati.
            </Text>
            
            <Text
              color="gray.400"
              fontSize={{ base: "sm", md: "md" }}
              lineHeight="tall"
              textAlign="center"
            >
              Insieme abbiamo gironzolato tra i mercati di mezzo mondo, provato i cibi più strani, sfidato la sorte in più di un'occasione. Abbiamo raccolto ricette e sapori dei posti che abbiamo amato, e ora siamo pronte per farli assaggiare a chi vuol girare il mondo...un piatto alla volta!
            </Text>
          </VStack>

          {/* Photo Gallery */}
          <Grid
            templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }}
            gap={4}
            mt={12}
          >
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              overflow="hidden"
              aspectRatio={1}
            >
              <Image
                src="/chisiamo/1.jpg"
                alt="Ilaria e Federica"
                objectFit="cover"
                w="100%"
                h="100%"
                filter="grayscale(30%)"
                _hover={{ filter: "grayscale(0%)", transform: "scale(1.05)" }}
                transition="all 0.3s ease"
              />
            </MotionBox>
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              overflow="hidden"
              aspectRatio={1}
            >
              <Image
                src="/chisiamo/2.jpg"
                alt="Ilaria e Federica"
                objectFit="cover"
                w="100%"
                h="100%"
                filter="grayscale(30%)"
                _hover={{ filter: "grayscale(0%)", transform: "scale(1.05)" }}
                transition="all 0.3s ease"
              />
            </MotionBox>
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              overflow="hidden"
              aspectRatio={1}
            >
              <Image
                src="/chisiamo/3.jpg"
                alt="Ilaria e Federica"
                objectFit="cover"
                w="100%"
                h="100%"
                filter="grayscale(30%)"
                _hover={{ filter: "grayscale(0%)", transform: "scale(1.05)" }}
                transition="all 0.3s ease"
              />
            </MotionBox>
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              overflow="hidden"
              aspectRatio={1}
            >
              <Image
                src="/chisiamo/4.jpg"
                alt="Ilaria e Federica"
                objectFit="cover"
                w="100%"
                h="100%"
                filter="grayscale(30%)"
                _hover={{ filter: "grayscale(0%)", transform: "scale(1.05)" }}
                transition="all 0.3s ease"
              />
            </MotionBox>
          </Grid>
        </MotionBox>
      </Box>

      {/* World Map Section - Past Events */}
      <Box
        ref={cucineRef}
        py={{ base: 16, md: 24 }}
        px={0}
        position="relative"
      >
        <VStack spacing={6} mb={12} px={6}>
          <MotionHeading
            as="h2"
            fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
            fontWeight="500"
            letterSpacing="wider"
            textAlign="center"
            color="white"
            textTransform="uppercase"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Le nostre cene dal mondo
          </MotionHeading>
          <MotionBox
            w={{ base: "40px", md: "60px" }}
            h="1px"
            bg="white"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          />
          <MotionText
            color="gray.500"
            fontSize={{ base: "xs", md: "sm" }}
            textTransform="uppercase"
            letterSpacing="widest"
            textAlign="center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Le cucine che abbiamo esplorato con voi
          </MotionText>
        </VStack>

        {/* Passport Button */}
        {user && (
          <MotionBox
            w="100%"
            maxW="1200px"
            mx="auto"
            mb={4}
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Flex justify="center">
              <Button
                onClick={() => setPassportOpen(true)}
                px={{ base: 4, md: 8 }}
                py={{ base: 3, md: 4 }}
                bg="transparent"
                border="1px solid"
                borderColor="whiteAlpha.300"
                borderRadius="none"
                w={{ base: "200px", md: "220px" }}
                transition="all 0.3s ease"
                color="white"
                fontSize={{ base: "2xs", md: "sm" }}
                fontWeight="400"
                letterSpacing="widest"
                _hover={{
                  bg: "white",
                  borderColor: "white",
                  color: "gray.900",
                }}
                leftIcon={
                  <Box as="svg" width="18px" height="18px" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                  </Box>
                }
              >
                IL MIO PASSAPORTO
              </Button>
            </Flex>
          </MotionBox>
        )}

        <MotionBox
          w="100%"
          maxW="1200px"
          mx="auto"
          position="relative"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          px={0}
          border="none"
          boxShadow="none"
          outline="none"
        >
          {/* World Map with D3.js */}
          <WorldMap 
            events={eventi}
            userPastEventIds={userPastEventIds}
            userFutureEventIds={userFutureEventIds}
            isUserLoggedIn={!!user}
          />
        </MotionBox>
      </Box>

      {/* Transition Section 2 */}
      <Box
        h={{ base: "15vh", md: "20vh" }}
        position="relative"
        overflow="hidden"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <MotionBox
          position="absolute"
          left={0}
          top="50%"
          h="1px"
          bg="linear-gradient(to right, transparent, white)"
          initial={{ width: 0 }}
          whileInView={{ width: "40%" }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
        <MotionBox
          position="absolute"
          right={0}
          top="50%"
          h="1px"
          bg="linear-gradient(to left, transparent, white)"
          initial={{ width: 0 }}
          whileInView={{ width: "40%" }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
        <MotionBox
          initial={{ scale: 0, rotate: 45 }}
          whileInView={{ scale: 1, rotate: 45 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8, type: "spring", stiffness: 200 }}
        >
          <Box
            w={{ base: "8px", md: "10px" }}
            h={{ base: "8px", md: "10px" }}
            bg="white"
            transform="rotate(45deg)"
          />
        </MotionBox>
      </Box>

      {/* Events Section */}
      <Box
        ref={serateRef}
        minH="100vh"
        py={{ base: 16, md: 24 }}
        px={6}
        position="relative"
      >
        {/* Section Title */}
        <VStack spacing={6} mb={16}>
          <MotionHeading
            as="h2"
            fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
            fontWeight="500"
            letterSpacing="wider"
            textAlign="center"
            color="white"
            textTransform="uppercase"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Prossime Serate
          </MotionHeading>
          <MotionBox
            w={{ base: "40px", md: "60px" }}
            h="1px"
            bg="white"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          />
        </VStack>

        {/* Events Grid */}
        {loadingEventi ? (
          <Flex justify="center" py={20}>
            <Spinner size="lg" color="white" thickness="1px" />
          </Flex>
        ) : eventi.filter(e => e.Attivo && new Date(e.DataEvento) >= new Date()).length === 0 ? (
          <Text
            textAlign="center"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="widest"
            fontSize="sm"
          >
            Nessun evento in programma
          </Text>
        ) : (
          <VStack
            spacing={8}
            maxW={{ base: "100%", md: "1000px" }}
            mx="auto"
            w="full"
          >
            {eventi
              .filter(e => e.Attivo && new Date(e.DataEvento) >= new Date())
              .map((evento, index) => (
                <EventCard key={evento.EventoId} evento={evento} index={index} onBook={handleBook} isAdmin={user?.userDetails === AUTHORIZED_EMAIL} />
              ))}
          </VStack>
        )}
      </Box>

      {/* Transition Section 3 */}
      <Box
        h={{ base: "15vh", md: "20vh" }}
        position="relative"
        overflow="hidden"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <MotionBox
          position="absolute"
          left={0}
          top="50%"
          h="1px"
          bg="linear-gradient(to right, transparent, white)"
          initial={{ width: 0 }}
          whileInView={{ width: "40%" }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
        <MotionBox
          position="absolute"
          right={0}
          top="50%"
          h="1px"
          bg="linear-gradient(to left, transparent, white)"
          initial={{ width: 0 }}
          whileInView={{ width: "40%" }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
        <MotionBox
          initial={{ scale: 0, rotate: 45 }}
          whileInView={{ scale: 1, rotate: 45 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8, type: "spring", stiffness: 200 }}
        >
          <Box
            w={{ base: "8px", md: "10px" }}
            h={{ base: "8px", md: "10px" }}
            bg="white"
            transform="rotate(45deg)"
          />
        </MotionBox>
      </Box>

      {/* Contatti Section */}
      <Box
        ref={contattiRef}
        py={{ base: 16, md: 24 }}
        px={6}
        position="relative"
      >
        <VStack spacing={6} mb={12}>
          <MotionHeading
            as="h2"
            fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
            fontWeight="500"
            letterSpacing="wider"
            textAlign="center"
            color="white"
            textTransform="uppercase"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Contatti
          </MotionHeading>
          <MotionBox
            w={{ base: "40px", md: "60px" }}
            h="1px"
            bg="white"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          />
        </VStack>

        <MotionBox
          maxW="700px"
          mx="auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <VStack spacing={6} align="center">
            <VStack spacing={3}>
              <Text
                color="white"
                fontSize={{ base: "sm", md: "md" }}
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Via Dalmazia 17, Brescia
              </Text>
              <Link
                href="tel:+393517934768"
                color="gray.400"
                fontSize={{ base: "sm", md: "md" }}
                _hover={{ color: "white" }}
                textTransform="uppercase"
                letterSpacing="wide"
              >
                +39 351 793 4768
              </Link>
              <Link
                href="mailto:info@scarpettahomerestaurant.it"
                color="gray.400"
                fontSize={{ base: "sm", md: "md" }}
                _hover={{ color: "white" }}
              >
                info@scarpettahomerestaurant.it
              </Link>
            </VStack>

            <HStack spacing={6} pt={4}>
              <Link
                href="https://www.facebook.com/profile.php?id=61567519595364"
                isExternal
                color="gray.400"
                _hover={{ color: "white", transform: "scale(1.1)" }}
                transition="all 0.2s"
                display="inline-block"
              >
                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M504 256C504 119 393 8 256 8S8 119 8 256c0 123.78 90.69 226.38 209.25 245V327.69h-63V256h63v-54.64c0-62.15 37-96.48 93.67-96.48 27.14 0 55.52 4.84 55.52 4.84v61h-31.28c-30.8 0-40.41 19.12-40.41 38.73V256h68.78l-11 71.69h-57.78V501C413.31 482.38 504 379.78 504 256z"></path></svg>
              </Link>
              <Link
                href="https://www.instagram.com/scarpetta.homerestaurant/"
                isExternal
                color="gray.400"
                _hover={{ color: "white", transform: "scale(1.1)" }}
                transition="all 0.2s"
                display="inline-block"
              >
                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"></path></svg>
              </Link>
            </HStack>
          </VStack>
        </MotionBox>
      </Box>
      </Box>

      {/* Booking Modal */}
      {selectedEvento && (
        <BookingModal
          evento={selectedEvento}
          isWaitingList={isWaitingList}
          onClose={handleBookingClose}
          onSuccess={handleBookingSuccess}
          isAdminView={user?.userDetails === AUTHORIZED_EMAIL}
        />
      )}

      {/* Gift Card Modal */}
      {giftCardOpen && (
        <GiftCardModal
          onClose={() => setGiftCardOpen(false)}
          onSuccess={() => {
            setGiftCardOpen(false);
            toast({
              duration: 5000,
              position: "bottom",
              render: () => (
                <Box
                  bg="gray.800"
                  color="gray.300"
                  px={4}
                  py={3}
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="widest"
                >
                  <Text mb={1}>Richiesta gift card ricevuta</Text>
                  <Text color="gray.500" fontSize="2xs">Riceverai il codice via email dopo l'approvazione</Text>
                </Box>
              ),
            });
          }}
        />
      )}

      {/* Passport Modal */}
      <PassportModal
        isOpen={passportOpen}
        onClose={() => setPassportOpen(false)}
        visitedEvents={visitedEvents}
        allEvents={eventi}
      />

      {/* Newsletter Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(8px)" />
        <ModalContent bg="gray.900" borderRadius="none" border="1px solid" borderColor="whiteAlpha.200" mx={4}>
          <ModalHeader
            color="white"
            fontWeight="400"
            textTransform="uppercase"
            letterSpacing="widest"
            fontSize="sm"
            borderBottom="1px solid"
            borderColor="whiteAlpha.100"
            py={4}
          >
            ISCRIVITI ALLA NEWSLETTER
          </ModalHeader>
          <ModalCloseButton color="gray.400" _hover={{ color: "white" }} />
          <ModalBody py={6}>
            <Text color="gray.500" mb={6} fontSize="xs" textTransform="uppercase" letterSpacing="wide">
              Ricevi aggiornamenti sulle prossime serate e menù esclusivi.
            </Text>
            <form onSubmit={handleNewsletterSubmit}>
              <VStack spacing={5}>
                <FormControl isRequired>
                  <FormLabel color="gray.400" fontSize="xs" textTransform="uppercase" letterSpacing="wide" mb={2}>
                    NOME
                  </FormLabel>
                  <Input
                    value={newsletterNome}
                    onChange={(e) => setNewsletterNome(e.target.value)}
                    placeholder="Il tuo nome"
                    bg="gray.900"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                    borderRadius="none"
                    _hover={{ borderColor: "whiteAlpha.400" }}
                    _focus={{ borderColor: "white", boxShadow: "0 0 0 1px white" }}
                    _placeholder={{ color: "gray.600" }}
                    color="white"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color="gray.400" fontSize="xs" textTransform="uppercase" letterSpacing="wide" mb={2}>
                    EMAIL
                  </FormLabel>
                  <Input
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="la-tua@email.com"
                    bg="gray.900"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                    borderRadius="none"
                    _hover={{ borderColor: "whiteAlpha.400" }}
                    _focus={{ borderColor: "white", boxShadow: "0 0 0 1px white" }}
                    _placeholder={{ color: "gray.600" }}
                    color="white"
                  />
                </FormControl>

                <Box w="full" h="1px" bg="whiteAlpha.200" my={1} />

                <VStack align="stretch" spacing={3} w="full">
                  <Checkbox
                    isChecked={newsletterUpdates}
                    onChange={(e) => setNewsletterUpdates(e.target.checked)}
                    colorScheme="whiteAlpha"
                    borderColor="whiteAlpha.400"
                    sx={{
                      ".chakra-checkbox__control": {
                        borderRadius: "none",
                        borderColor: "whiteAlpha.400",
                        bg: "transparent",
                        _checked: {
                          bg: "white",
                          borderColor: "white",
                          color: "gray.900",
                        },
                      },
                    }}
                  >
                    <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide">
                      Voglio ricevere aggiornamenti su date speciali e prenotazioni anticipate *
                    </Text>
                  </Checkbox>
                  <Checkbox
                    isChecked={newsletterPrivacy}
                    onChange={(e) => setNewsletterPrivacy(e.target.checked)}
                    colorScheme="whiteAlpha"
                    borderColor="whiteAlpha.400"
                    isRequired
                    sx={{
                      ".chakra-checkbox__control": {
                        borderRadius: "none",
                        borderColor: "whiteAlpha.400",
                        bg: "transparent",
                        _checked: {
                          bg: "white",
                          borderColor: "white",
                          color: "gray.900",
                        },
                      },
                    }}
                  >
                    <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide">
                      Acconsento al trattamento dei miei dati ai sensi della normativa sulla privacy *
                    </Text>
                  </Checkbox>
                </VStack>

                <Button
                  type="submit"
                  w="full"
                  bg="white"
                  color="gray.900"
                  borderRadius="none"
                  _hover={{ bg: "gray.200" }}
                  isLoading={isSubmitting}
                  loadingText="ISCRIZIONE..."
                  textTransform="uppercase"
                  letterSpacing="wide"
                  fontSize="xs"
                  isDisabled={!newsletterUpdates || !newsletterPrivacy}
                  mt={2}
                >
                  ISCRIVITI
                </Button>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

// Event card component
const EventCard: React.FC<{
  evento: Evento;
  index: number;
  onBook: (evento: Evento, waitingList: boolean) => void;
  isAdmin: boolean;
}> = ({ evento, index, onBook, isAdmin }) => {
  const date = new Date(evento.DataEvento);
  const dayName = date.toLocaleDateString('it-IT', { weekday: 'long' });
  const day = date.getDate();
  const month = date.toLocaleDateString('it-IT', { month: 'long' });
  const year = date.getFullYear();
  const time = date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  const disponibili = evento.PostiTotali - evento.PostiOccupati;
  const isEsaurito = disponibili <= 0;
  // Public visitors only see month/year when the exact day is flagged as hidden;
  // the admin always sees the full date since they need it to manage the event.
  const showExactDate = isAdmin || evento.MostraGiornoEsatto !== false;

  return (
    <MotionBox
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      w="full"
    >
      <Grid
        templateColumns={{ base: "1fr", md: evento.ImmagineUrl ? "1fr 1fr" : "1fr" }}
        gap={{ base: 4, md: 6 }}
      >
        {/* Card Content */}
        <Box
          p={{ base: 6, md: 8 }}
          display="flex"
          flexDirection="column"
          border="1px solid"
          borderColor="whiteAlpha.200"
          transition="all 0.3s ease"
          _hover={{
            borderColor: "whiteAlpha.400",
          }}
        >
          {/* Date & Time */}
          <HStack spacing={3} mb={showExactDate ? 2 : 5}>
            <Text
              color="gray.500"
              fontSize="xs"
              textTransform="uppercase"
              letterSpacing="widest"
            >
              {showExactDate ? `${dayName} ${day} ${month} ${year}` : `${month} ${year}`}
            </Text>
            {isAdmin && !showExactDate && (
              <Text color="yellow.400" fontSize="xs" textTransform="uppercase" letterSpacing="wide">
                (data nascosta al pubblico)
              </Text>
            )}
          </HStack>

          {showExactDate && (
            <Text
              color="white"
              fontSize="sm"
              fontWeight="500"
              letterSpacing="wide"
              mb={5}
            >
              ORE {time}
            </Text>
          )}

          {/* Title */}
          <Heading
            as="h3"
            fontSize={{ base: "xl", md: "2xl" }}
            fontWeight="500"
            color="white"
            textTransform="uppercase"
            letterSpacing="wide"
            mb={4}
            lineHeight="shorter"
          >
            {evento.Titolo}
          </Heading>

          {/* Menu Preview */}
          <Box
            mb={6}
            flex={1}
            color="gray.400"
            fontSize="sm"
            lineHeight="tall"
            sx={{
              'p': { mb: 2 },
              'ul, ol': { pl: 0, listStyle: 'none' },
              'li': { mb: 1 },
              'strong, b': { color: 'white', fontWeight: '500' },
            }}
            dangerouslySetInnerHTML={{ __html: evento.Menu }}
          />

          {/* Divider */}
          <Box w="30px" h="1px" bg="whiteAlpha.300" mb={5} />

          {/* Price info */}
          <VStack align="flex-start" spacing={3} mb={5}>
            <VStack align="flex-start" spacing={0.5}>
              <HStack spacing={2}>
                <Text color="gray.600" fontSize="xs" textTransform="uppercase" letterSpacing="wide">
                  Analcolico
                </Text>
                <Text color="white" fontSize="sm" fontWeight="500">
                  €{evento.PrezzoAnalcolico}
                </Text>
              </HStack>
              {evento.AnalcolicoDesc && (
                <Text color="gray.500" fontSize="xs">
                  {evento.AnalcolicoDesc}
                </Text>
              )}
            </VStack>
            <VStack align="flex-start" spacing={0.5}>
              <HStack spacing={2}>
                <Text color="gray.600" fontSize="xs" textTransform="uppercase" letterSpacing="wide">
                  Alcolico
                </Text>
                <Text color="white" fontSize="sm" fontWeight="500">
                  €{evento.PrezzoAlcolico}
                </Text>
              </HStack>
              {evento.AlcolicoDesc && (
                <Text color="gray.500" fontSize="xs">
                  {evento.AlcolicoDesc}
                </Text>
              )}
            </VStack>
          </VStack>

          {/* Availability */}
          <Text
            fontSize="xs"
            textTransform="uppercase"
            letterSpacing="widest"
            color={isEsaurito ? "gray.600" : "white"}
            mb={6}
          >
            {isEsaurito ? "LISTA D'ATTESA" : `${disponibili} POSTI DISPONIBILI SU ${evento.PostiTotali}`}
          </Text>

          {/* Book Button */}
          {!evento.Prenotabile ? (
            <Text
              w="full"
              textAlign="center"
              py={6}
              fontSize="sm"
              fontWeight="400"
              letterSpacing="widest"
              textTransform="uppercase"
              color="gray.500"
              bg="blackAlpha.500"
              backdropFilter="blur(8px)"
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              PRENOTABILE PROSSIMAMENTE
            </Text>
          ) : (
            <Button
              w="full"
              bg="blackAlpha.500"
              backdropFilter="blur(8px)"
              color="white"
              border="1px solid"
              borderColor="whiteAlpha.300"
              borderRadius="none"
              py={6}
              fontSize="sm"
              fontWeight="400"
              letterSpacing="widest"
              textTransform="uppercase"
              _hover={{
                bg: "white",
                color: "gray.900",
                borderColor: "white",
              }}
              onClick={() => onBook(evento, isEsaurito)}
            >
              {isEsaurito ? "ISCRIVITI ALLA LISTA D'ATTESA" : "PRENOTA ORA"}
            </Button>
          )}
        </Box>

        {/* Event Image - Right on desktop, below on mobile */}
        {evento.ImmagineUrl && (
          <Box
            h={{ base: "350px", md: "auto" }}
            minH={{ base: "350px", md: "100%" }}
            position="relative"
            overflow="hidden"
            border="1px solid"
            borderColor="whiteAlpha.200"
            transition="all 0.3s ease"
            _hover={{
              borderColor: "whiteAlpha.400",
            }}
          >
            <Image
              src={evento.ImmagineUrl}
              alt={evento.Titolo}
              position="absolute"
              top={0}
              left={0}
              w="100%"
              h="100%"
              objectFit="cover"
            />
          </Box>
        )}
      </Grid>
    </MotionBox>
  );
};

// Navigation button component
const NavButton: React.FC<{ label: string }> = ({ label }) => (
  <Flex
    align="center"
    justify="center"
    px={{ base: 4, md: 8 }}
    py={{ base: 3, md: 4 }}
    bg="blackAlpha.500"
    backdropFilter="blur(8px)"
    border="1px solid"
    borderColor="whiteAlpha.300"
    borderRadius="none"
    w={{ base: "160px", md: "180px" }}
    transition="all 0.3s ease"
    _hover={{
      bg: "white",
      borderColor: "white",
      "& p": { color: "gray.900" },
    }}
  >
    <Text
      color="white"
      fontSize={{ base: "2xs", md: "sm" }}
      fontWeight="400"
      letterSpacing="widest"
      whiteSpace="nowrap"
    >
      {label}
    </Text>
  </Flex>
);

export default LandingPage;
